import React, { Component, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
// import Router from './router';
import I18N from '../scripts/i18n';
import Analytics from './analytics';
import { Loader } from '@googlemaps/js-api-loader';
import TenantService from './services/tenantService';
import { ConfigContext } from './context/configContext';
import { getConstants} from './applicationConstants';

import {
  RouterProvider,
  createHashRouter,
  ScrollRestoration,
} from 'react-router-dom';

import SpinnerLoader from './components/loader/spinnerLoader';
const Home = lazy(() => import('./pages/home/home'));
const Details = lazy(() => import('./pages/details/details'));
const Results = lazy(() => import('./pages/results/results'));
const TabliteBooking = lazy(() => import('./pages/tabliteBooking/tabliteBooking'));

// MOCK SERVER FOR INTERCEPTING API CALLS.
// ADD ROUTES TO BE INTERCEPTED BELOW FOR
// DEVELOPMENT. REMOVE BEFORE DEPLOYING.

// if (process.env.NODE_ENV === 'development') {
//   const mockServer = createServer({
//     routes() {
//       this.urlPrefix = 'https://dev-locator-api.ecplocator-v2.net/api';
//       this.get('/GetIpLocator', () => import('../data/testIpLocatorData'));
//       this.post('/practice/search', () => import('../data/testPracticeResults.json'));
//       this.get('/practice/:tenant/:practiceId', () => import('../data/testPracticeDetails.json'));
//       this.get('/tenant/getUiConfiguration/:tenant', () => import('../data/testTenantUIOptions.json'));
//     }
//   });
//   mockServer.passthrough((req) => {
//     if (req.responseURL.includes('browser-sync')) {
//       return true;
//     }

//     return false;
//   });
// }

/**
 * Represents the entry point into the application.
 */
class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      config: {},
      isReadyToRender: false,
    };
    this._tenantService = new TenantService();
  }

  getGoogleAnalyticsTrackingId(options) {
    const {
      googleAnalyticsTrackingIdDev,
      googleAnalyticsTrackingIdQa,
      googleAnalyticsTrackingIdStage,
      googleAnalyticsTrackingIdProd,
    } = options;

    switch (process.env.NODE_ENV) {
      case 'development':
        return googleAnalyticsTrackingIdDev;
      case 'qa':
        return googleAnalyticsTrackingIdQa;
      case 'staging':
        return googleAnalyticsTrackingIdStage;
      case 'production':
        return googleAnalyticsTrackingIdProd;
    }
  }

  getTenantConfig = () => {
    let retryCounter = 0;
    const maxRetries = 5;

    const retry = async () => {
      try {
        return await this._tenantService.getTenantUIOptions();
      } catch (error) {
        if (++retryCounter < maxRetries) {
          setTimeout(retry, 1000);
        } else {
          console.error(error);
        }
      }
    };

    return retry;
  };

  /**
   * Executes when the component has mounted to the DOM.
   */
  async componentDidMount() {
    try {
      const tenantConfig = await this.getTenantConfig();
      const { options, labels } = await tenantConfig();
      const { enableAdobeAnalytics } = options;

      // Override tenant options here
      const config = {
        ...options,

        // Override RTL for non-arabic
        enable_rtl_orientation:
          !!options?.enable_rtl_orientation &&
          !getConstants().GLOBAL.CURRENT_LANGUAGE.includes('en'),

        // Disable Baidu map and its services
        use_baidu_map: false,

        // Minimum results set to 100
        results_per_page: Math.max(options?.results_per_page ?? 0, 100),
      };

      window.googleMapsApiLoader = new Loader({
        apiKey: 'AIzaSyBetlV7K9i6m_PvqLYBFGq9RHwZXzodfgE',
        version: 'weekly',
        libraries: ['core'],
      });

      window.googleMapsApiLoader
        .importLibrary('geocoding')
        .then(({ Geocoder }) => {
          window._geocoder = new Geocoder();
        })
        .catch((error) => {
          console.error(
            `Failed to import Geocoding library from Google Maps API: ${error}`
          );
        });

      const gaTrackingId = this.getGoogleAnalyticsTrackingId(options);
      Analytics.appendGoogleAnalyticsScript(gaTrackingId[0]);
      Analytics.appendGoogleAnalyticsV4Script(gaTrackingId[1]);

      if (enableAdobeAnalytics) {
        Analytics.appendAdobeAnalyticsScript();
      }

      I18N.init(labels);
      Analytics.init(gaTrackingId);

      this.setState(() => ({
        config,
        isReadyToRender: true,
      }));
    } catch (error) {
      console.error(error);
    }
  }

  router = createHashRouter([
    {
      path: '/',
      element: (
        <Suspense
          fallback={
            <div id="suspenseLoader">
              <SpinnerLoader show={false} />
            </div>
          }
        >
          <Home />
        </Suspense>
      ),
    },
    {
      path: '/result',
      element: (
        <Suspense
          fallback={
            <div id="suspenseLoader">
              <SpinnerLoader show={false} />
            </div>
          }
        >
          <Results />
        </Suspense>
      ),
    },
    {
      path: '/practice/:practiceId',
      element: (
        <Suspense
          fallback={
            <div id="suspenseLoader">
              <SpinnerLoader show={false} />
            </div>
          }
        >
          <Details />
        </Suspense>
      ),
    },
    {
      path: '/tabliteBooking/',
      element: (
        <Suspense
          fallback={
            <div id="suspenseLoader">
              <SpinnerLoader show={false} />
            </div>
          }
        >
          <TabliteBooking />
        </Suspense>
      ),
    },
  ]);

  /**
   * Renders the component.
   */
  render() {
    const { config, isReadyToRender } = this.state;

    return (
      <div>
        <ConfigContext.Provider value={config}>
          {isReadyToRender && (
            <RouterProvider router={this.router}>
              <ScrollRestoration />
            </RouterProvider>
          )}
        </ConfigContext.Provider>
      </div>
    );
  }
}

const app = document.getElementById('locatorv2embed');

if (app) {
  ReactDOM.render(<App />, app);
}

export default App;
